import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArticleFormContent } from './components/ArticleForm';
import { ArticleMetadata } from './components/ArticleMetadata';
import { ArticleSheetError } from './components/ArticleSheetError';
import { ArticleSheetHeader } from './components/ArticleSheetHeader';
import { ArticleSheetSkeleton } from './components/ArticleSheetSkeleton';
import { UnsavedChangesDialog } from './components/UnsavedChangesDialog';
import { useArticleSheet } from './hooks/useArticleSheet.hook';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { AutoRefreshConfirmDialog } from '@/Domain/components/KnowledgeBase/AutoRefresh/AutoRefreshConfirmDialog';
import { Form } from '@/Domain/components/Shared/Form/Form';
import { Sheet, SheetContent } from '@/Domain/components/Shared/Sheet/sheet';
import { useSocket } from '@/Domain/components/SocketProvider';
import { ArticleStateChange, ArticleStateEnum } from '@/Domain/components/TopBar/types';
import { useArticleAutoRefresh } from '@/Domain/hooks/KnowledgeBase/useArticleAutoRefresh.hook';
import { useRefreshArticle } from '@/Domain/hooks/KnowledgeBase/useRefreshArticle.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { isRefreshableArticleType } from '@/Domain/utils/article';
import {
  markAutoRefreshWarningShown,
  shouldShowAutoRefreshWarning,
} from '@/Domain/utils/autoRefreshWarning';

export const ArticleSheet = () => {
  const { t } = useTranslation(['knowledge_base']);
  const { isOpen, toggleModal, closeModal } = useModalStore();
  const navigate = useNavigate();
  const { scenario, id: articleIdFromParams } = useParams<{ scenario?: string; id?: string }>();
  const {
    form,
    article,
    isArticleUnavailable,
    loadFailureReason,
    isLoading,
    markLocalEdit,
    createSubmitHandler,
  } = useArticleSheet();
  const { refreshArticle, isPending: isMutationPending } = useRefreshArticle();
  const { toggle: toggleAutoRefresh, togglingIds } = useArticleAutoRefresh();
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  // Auto-refreshing URL articles are source-managed, so the content is locked:
  // turning auto-refresh off (banner button or sidebar toggle) unlocks editing.
  const isLockedByAutoRefresh =
    !!article.data &&
    isRefreshableArticleType(article.data.type) &&
    article.data.auto_refresh_enabled;
  const isUnlocking = !!article.data && togglingIds.has(String(article.data.id));

  const suppressCloseRef = useRef(false);
  const hasLocalChangesRef = useRef(false);
  const refreshTriggeredFromSheetRef = useRef(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isRefreshInProgress, setIsRefreshInProgress] = useState(false);
  const [autoRefreshConfirmOpen, setAutoRefreshConfirmOpen] = useState(false);
  const pendingAutoRefreshIdRef = useRef<number | null>(null);

  // Combined refresh state: either mutation is pending OR background refresh is in progress
  const isRefreshing = isMutationPending || isRefreshInProgress;

  // Listen to socket events for refresh state changes
  useEffect(() => {
    // Use articleIdFromParams for consistency with query key (which uses params id)
    if (!articleIdFromParams) return;

    const handleArticleStateChanged = (data: ArticleStateChange) => {
      if (String(data.article_id) !== articleIdFromParams) {
        return;
      }

      // Set refresh in progress when receiving REFRESHING state
      if (data.state === ArticleStateEnum.REFRESHING) {
        setIsRefreshInProgress(true);
        return;
      }

      // Saving closes the sheet, so this covers an article tagged while it is
      // open: reopened inside the window, or reached by a bulk or scrape run.
      if (data.state === ArticleStateEnum.AUTO_TAGGED && data.tags?.length) {
        // apply_auto_tags only ever writes to an untagged article, so any row here
        // is the user's and must win — a blank one is a tag they are mid-way typing.
        if ((form.getValues('tagsObjects') ?? []).length > 0) return;

        // TagsField's own idiom: paint without dirtying the form. `tags` moves in
        // lockstep because useArticleSheet reads it as the save baseline.
        const options = { shouldDirty: false, shouldTouch: false };
        form.setValue('tags', data.tags, options);
        form.setValue(
          'tagsObjects',
          data.tags.map((value) => ({ value })),
          options,
        );
        return;
      }

      // Clear refresh state and show results on completion
      if (
        data.state === ArticleStateEnum.DONE ||
        data.state === ArticleStateEnum.ERROR ||
        data.state === ArticleStateEnum.REFRESH_FAILED
      ) {
        const wasTriggeredFromSheet = refreshTriggeredFromSheetRef.current;
        setIsRefreshInProgress(false);
        refreshTriggeredFromSheetRef.current = false;

        // Invalidate article query using params id to ensure query key match
        queryClient.invalidateQueries({
          queryKey: [QueryKey.knowledgeBase.article, articleIdFromParams],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.knowledgeBase.articlesInfinite],
        });

        // Only show toast if refresh was triggered from this sheet (avoid duplicate with KnowledgeManager)
        if (wasTriggeredFromSheet) {
          if (data.state === ArticleStateEnum.DONE) {
            toast.success(t('knowledge_base:article.toast.refresh_completed'));
          } else {
            toast.error(t('knowledge_base:article.toast.refresh_failed'));
          }
        }
      }
    };

    socket.on(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);
    };
  }, [articleIdFromParams, socket, queryClient, t, form]);

  const SHEET_ANIMATION_DURATION = 320;

  const navigateToKnowledgeBase = useCallback(() => {
    const path = scenario
      ? `/i/${scenario}/${IndexPath.knowledgeBase.root}`
      : `/i/${IndexPath.knowledgeBase.root}`;
    navigate(path);
  }, [navigate, scenario]);

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) {
      toggleModal(MODAL_NAMES.ARTICLE_SHEET);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track local changes once dirty
  const {
    formState: { isDirty: isFormDirty },
  } = form;
  useEffect(() => {
    if (isFormDirty) {
      hasLocalChangesRef.current = true;
    } else {
      // When the form is reset back to clean state (e.g., after save), clear the flag
      hasLocalChangesRef.current = false;
    }
  }, [isFormDirty]);

  // Once the token is gone, every way out of this sheet has to leave the authenticated tree,
  // not just the Log in button: AuthGate snapshots `isAuthenticated` at mount, so an SPA hop
  // would land the user on a Knowledge Base list that fails exactly the same way. A full
  // navigation lets Django's index() serve the login screen.
  const isSessionExpired = loadFailureReason === 'session_expired';
  const leaveSheet = useCallback(() => {
    if (isSessionExpired) {
      window.location.assign('/');
      return;
    }
    closeModal();
    setTimeout(() => {
      navigateToKnowledgeBase();
    }, SHEET_ANIMATION_DURATION);
  }, [isSessionExpired, closeModal, navigateToKnowledgeBase]);

  // Sheet open/close handler
  const handleSheetOpenChange = useCallback(
    (open: boolean) => {
      // Ignore attempts to close the sheet while confirmation dialog is active
      if (suppressCloseRef.current && !open) return;
      if (!open) {
        // A dead session does not make a draft worth less: still ask before discarding it.
        if (isFormDirty || hasLocalChangesRef.current) {
          suppressCloseRef.current = true;
          setConfirmationModalOpen(true);
          return;
        }

        leaveSheet();
      }
    },
    [isFormDirty, leaveSheet],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmationModalOpen || autoRefreshConfirmOpen) return;

        const target = e.target as HTMLElement | null;
        if (target && target.closest('#article_tags')) {
          e.preventDefault();
          e.stopPropagation();
          (document.activeElement as HTMLElement | null)?.blur();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        handleSheetOpenChange(false);
      }
    },
    // handleSheetOpenChange belongs here: without it this listener keeps the callback built on
    // the first render, and a session that dies while the sheet is open would still be escaped
    // by an SPA hop into a tree whose token is gone. The dirty guard survived the omission only
    // because it reads a ref; `isSessionExpired` is a plain boolean and does not.
    [confirmationModalOpen, autoRefreshConfirmOpen, handleSheetOpenChange],
  );

  // Global ESC key handling
  useEffect(() => {
    if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.ARTICLE_SHEET), handleKeyDown]);

  // Confirmation dialog handler
  const handleConfirmationOpenChange = useCallback(
    (open: boolean) => {
      setConfirmationModalOpen(open);
      if (!open) {
        // Defer clearing suppression so the same ESC event doesn't also close the Sheet
        setTimeout(() => {
          suppressCloseRef.current = false;
        }, 0);
      } else {
        suppressCloseRef.current = true;
      }
    },
    [setConfirmationModalOpen],
  );

  // Form submit handler
  const handleSave = useCallback(() => {
    const submit = createSubmitHandler({
      onSuccess: () => {
        toast.success(t('knowledge_base:article.toast.update_successfully'));
        closeModal();
        setTimeout(() => {
          navigateToKnowledgeBase();
        }, SHEET_ANIMATION_DURATION);
      },
      onError: () => {
        toast.error(t('knowledge_base:article.toast.update_failed'));
      },
    });
    form.handleSubmit(submit)();
  }, [createSubmitHandler, form, closeModal, navigateToKnowledgeBase, t]);

  // Save hotkey: Cmd+S / Ctrl+S
  useHotkeys(
    ['meta+s', 'ctrl+s'],
    (event) => {
      // Only when the Article sheet is open
      if (!isOpen(MODAL_NAMES.ARTICLE_SHEET)) return;
      event.preventDefault();
      event.stopPropagation();
      if (!confirmationModalOpen && !isLoading) {
        handleSave();
      }
    },
    [isOpen, confirmationModalOpen, isLoading, handleSave],
    { enableOnFormTags: true, enableOnContentEditable: true },
  );

  // Confirmation dialog confirm handler
  const handleConfirmClose = useCallback(() => {
    setConfirmationModalOpen(false);
    form.reset();
    hasLocalChangesRef.current = false;
    leaveSheet();
  }, [form, leaveSheet]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (articleIdFromParams) {
      refreshTriggeredFromSheetRef.current = true;
      setIsRefreshInProgress(true);
      refreshArticle(Number(articleIdFromParams));
    }
  }, [articleIdFromParams, refreshArticle]);

  // Enabling re-scrapes the page (overwriting manual edits), so confirm it at
  // most once per week before turning the toggle on; disabling stays direct.
  const handleRequestEnableAutoRefresh = useCallback(
    (id: number) => {
      if (shouldShowAutoRefreshWarning()) {
        pendingAutoRefreshIdRef.current = id;
        setAutoRefreshConfirmOpen(true);
      } else {
        toggleAutoRefresh(id, true);
      }
    },
    [toggleAutoRefresh],
  );

  const handleConfirmEnableAutoRefresh = useCallback(() => {
    markAutoRefreshWarningShown();
    if (pendingAutoRefreshIdRef.current != null) {
      toggleAutoRefresh(pendingAutoRefreshIdRef.current, true);
    }
    pendingAutoRefreshIdRef.current = null;
    setAutoRefreshConfirmOpen(false);
  }, [toggleAutoRefresh]);

  // Form keydown handler
  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If ESC is pressed inside Tags, only blur the focused input.
        const target = e.target as HTMLElement | null;
        if (target && target.closest('#article_tags')) {
          e.preventDefault();
          e.stopPropagation();
          (document.activeElement as HTMLElement | null)?.blur();
          return;
        }

        // Mirror the X button behavior instead of letting Radix close the Sheet directly
        e.preventDefault();
        e.stopPropagation();
        if (!confirmationModalOpen && !autoRefreshConfirmOpen) handleSheetOpenChange(false);
      }
    },
    [confirmationModalOpen, autoRefreshConfirmOpen, handleSheetOpenChange],
  );

  return (
    <Form {...form}>
      <Sheet open={isOpen(MODAL_NAMES.ARTICLE_SHEET)} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          className="!max-w-[100vw] xl:!max-w-[80%] 2xl:!max-w-6xl w-full"
          tabIndex={undefined}
          asChild
        >
          <form
            className="flex h-full flex-col pb-0"
            onInputCapture={markLocalEdit}
            onKeyDownCapture={handleFormKeyDown}
          >
            <ArticleSheetHeader
              onSave={handleSave}
              onClose={() => handleSheetOpenChange(false)}
              isLoading={isLoading}
            />

            <div className="mt-0 min-h-0 flex-1 overflow-y-auto lg:overflow-hidden">
              {article.isPending ? (
                <ArticleSheetSkeleton />
              ) : isArticleUnavailable ? (
                <ArticleSheetError
                  // isArticleUnavailable already implies a load error, so the fallback is
                  // unreachable; it names the one reason that keeps the pre-existing
                  // affordance rather than inventing a dead end out of an impossible state.
                  reason={loadFailureReason ?? 'backend_error'}
                  isRetrying={article.isFetching}
                  onRetry={() => void article.refetch()}
                  onClose={() => handleSheetOpenChange(false)}
                  // Same dirty-aware exit as every other dismissal, so Log in cannot be the
                  // one control that discards a draft without asking.
                  onSignIn={() => handleSheetOpenChange(false)}
                />
              ) : (
                <div className="grid lg:h-full min-h-0 grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.4fr)]">
                  <ArticleFormContent
                    form={form}
                    readOnly={isLockedByAutoRefresh}
                    onUnlock={() => article.data && toggleAutoRefresh(article.data.id, false)}
                    isUnlocking={isUnlocking}
                  />
                  <div className="custom-scroll lg:h-full min-h-0 min-w-0 pl-1 lg:overflow-y-auto lg:border-l lg:border-border">
                    <ArticleMetadata
                      article={article.data}
                      form={form}
                      onRefresh={handleRefresh}
                      isRefreshing={isRefreshing}
                      onRequestEnable={handleRequestEnableAutoRefresh}
                    />
                  </div>
                </div>
              )}
            </div>

            <UnsavedChangesDialog
              open={confirmationModalOpen}
              onOpenChange={handleConfirmationOpenChange}
              onConfirmClose={handleConfirmClose}
            />

            <AutoRefreshConfirmDialog
              placement="sheet"
              open={autoRefreshConfirmOpen}
              onOpenChange={setAutoRefreshConfirmOpen}
              onConfirm={handleConfirmEnableAutoRefresh}
            />
          </form>
        </SheetContent>
      </Sheet>
    </Form>
  );
};
